/**
 * The Results console — the module landing. The by-id lookup is kept (open a CR / jump to emission by
 * id, re-open from the local per-lab "recently consulted" set), but LAB-08 adds a server-backed
 * "à émettre" WORKLIST so a biologiste reaches the emission queue WITHOUT knowing a numeric id:
 *   - the "à émettre" queue (validated demandes with no/stale CR) — capability-scoped (emit),
 *   - open an existing compte-rendu by id,
 *   - jump to emission from a (validated) demande id,
 *   - re-open from the local per-lab "recently consulted" set (non-clinical handles only).
 * 5 states: offline (banner via useUiStore); the lookup is always interactive; the worklist + recents
 * areas show their own loading/error/empty (and a graceful "not deployed" notice when the pending
 * endpoint is absent). Forbidden is handled by the route's ProtectedRoute (results.compterendu.read).
 */
import { useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Badge, Button, Card, EmptyState, ErrorState, FormField, Input, LoadingState, Table, type Column } from "@/ui";
import { useToast } from "@/ui";
import { useT, useUiStore } from "@/app";
import { useCan } from "@/auth";
import { useAuthStore } from "@/auth";
import { ApiError } from "@/api";
import { PageHeader } from "./components/PageHeader";
import { EtatBadge } from "./components/StatusBadges";
import { RESULTS_CAPS } from "./caps";
import { usePendingEmissions } from "./hooks";
import { useRecentComptesRendus } from "./recent";
import { digitsOnly, formatDateTime } from "./format";
import type { PendingEmissionLine } from "./types";

/**
 * True when the /results/pending endpoint is not deployed on this install (404/501/NOT_FOUND). The
 * worklist is a convenience over the always-present id-lookup — an absent endpoint degrades to a notice,
 * never a hard error (the queryClient already treats 404/501 as terminal, so this never retries).
 */
function isPendingAbsent(error: unknown): boolean {
  return (
    error instanceof ApiError &&
    !error.isForbidden &&
    !error.isUnauthenticated &&
    (error.httpStatus === 404 || error.httpStatus === 501 || error.code === "NOT_FOUND")
  );
}

export function ResultsConsolePage() {
  const { t, locale } = useT();
  const navigate = useNavigate();
  const toast = useToast();
  const online = useUiStore((s) => s.online);
  const canEmit = useCan(RESULTS_CAPS.create);
  const labId = useAuthStore((s) => s.user?.laboratoireId ?? s.lab?.id ?? null);
  const { recents, clear } = useRecentComptesRendus(labId);
  // LAB-08 — the "à émettre" worklist. Only an emitter needs it (and the server re-checks the cap), and
  // it is meaningless offline (no fetch), so `enabled` folds both in. An absent endpoint degrades below.
  const pending = usePendingEmissions(canEmit && online);
  const pendingRows = pending.data?.items ?? [];

  const [crId, setCrId] = useState("");
  const [demandeId, setDemandeId] = useState("");
  const [crError, setCrError] = useState<string | null>(null);
  const [demandeError, setDemandeError] = useState<string | null>(null);
  const crRef = useRef<HTMLInputElement>(null);

  const openCr = () => {
    const id = digitsOnly(crId);
    if (!id) {
      setCrError(t("results.lookup.invalidId"));
      crRef.current?.focus();
      return;
    }
    setCrError(null);
    navigate(`/resultats/comptes-rendus/${id}`);
  };

  const openEmission = () => {
    const id = digitsOnly(demandeId);
    if (!id) {
      setDemandeError(t("results.lookup.invalidId"));
      return;
    }
    setDemandeError(null);
    navigate(`/resultats/demandes/${id}/emettre`);
  };

  const clearRecents = () => {
    clear();
    toast.info(t("results.recent.clear"));
  };

  const reasonLabel = (line: PendingEmissionLine): string =>
    line.reason === "resultats_neufs"
      ? t("results.pending.reason.resultats_neufs", { version: line.last_emitted_version ?? "—" })
      : t("results.pending.reason.jamais_emis");

  const pendingColumns: Column<PendingEmissionLine>[] = [
    {
      key: "demande",
      header: t("results.pending.col.demande"),
      mono: true,
      cell: (l) => (
        <span className="nv-mono nv-ltr-island" style={{ fontWeight: 600 }}>
          {l.demande_reference ?? `#${l.demande_analyse_id}`}
        </span>
      ),
    },
    {
      key: "validated",
      header: t("results.pending.col.validated"),
      mono: true,
      align: "end",
      cell: (l) => <span className="nv-mono nv-ltr-island">{l.validated_count}</span>,
    },
    {
      key: "reason",
      header: t("results.pending.col.reason"),
      cell: (l) => (
        <Badge tone={l.reason === "jamais_emis" ? "warn" : "info"}>{reasonLabel(l)}</Badge>
      ),
    },
    {
      key: "lastValidation",
      header: t("results.pending.col.lastValidation"),
      cell: (l) => (
        <span style={{ color: "var(--nv-steel)", fontSize: "var(--nv-fs-caption)" }}>
          {formatDateTime(l.derniere_validation_at, locale)}
        </span>
      ),
    },
    {
      key: "emit",
      header: "",
      align: "end",
      cell: (l) => (
        <Button
          size="sm"
          leadingIcon="✚"
          disabled={!online}
          onClick={() => navigate(`/resultats/demandes/${l.demande_analyse_id}/emettre`)}
        >
          {t("results.pending.emit")}
        </Button>
      ),
    },
  ];

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: "var(--nv-s-5)" }}>
      <PageHeader title={t("results.console.title")} subtitle={t("results.console.subtitle")} />

      {/* LAB-08 — the server-backed "à émettre" worklist (emit-gated). Renders only for an emitter; an
          absent endpoint (404/501) degrades to a notice so the id-lookup below always stays usable. */}
      {canEmit && (
        <Card
          title={t("results.pending.title")}
          actions={
            pendingRows.length > 0 ? (
              <Badge tone="info">{t("results.pending.count", { n: pending.data?.total ?? pendingRows.length })}</Badge>
            ) : undefined
          }
        >
          <p style={{ marginBlockStart: 0, marginBlockEnd: "var(--nv-s-4)", color: "var(--nv-slate)" }}>
            {t("results.pending.subtitle")}
          </p>
          {isPendingAbsent(pending.error) ? (
            <p style={{ margin: 0, color: "var(--nv-slate)" }}>{t("results.pending.unavailable")}</p>
          ) : pending.isLoading ? (
            <LoadingState variant="table" rows={4} />
          ) : pending.isError ? (
            <ErrorState message={pending.error?.message ?? t("results.pending.error")} onRetry={() => void pending.refetch()} />
          ) : pendingRows.length === 0 ? (
            <EmptyState glyph="✓" title={t("results.pending.empty.title")} description={t("results.pending.empty.desc")} />
          ) : (
            <Table<PendingEmissionLine>
              columns={pendingColumns}
              rows={pendingRows}
              rowKey={(l) => l.demande_analyse_id}
              caption={t("results.pending.title")}
            />
          )}
        </Card>
      )}

      <Card title={t("results.lookup.legend")}>
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fit, minmax(260px, 1fr))",
            gap: "var(--nv-s-5)",
          }}
        >
          {/* open a CR by id */}
          <form
            onSubmit={(e) => {
              e.preventDefault();
              openCr();
            }}
            style={{ display: "flex", flexDirection: "column", gap: "var(--nv-s-3)" }}
          >
            <FormField
              label={t("results.lookup.crId")}
              hint={t("results.lookup.crIdHint")}
              {...(crError ? { error: crError } : {})}
            >
              {(p) => (
                <Input
                  {...p}
                  ref={crRef}
                  mono
                  inputMode="numeric"
                  autoComplete="off"
                  value={crId}
                  placeholder="123"
                  onChange={(e) => setCrId(e.target.value)}
                />
              )}
            </FormField>
            <Button type="submit" leadingIcon="▸">
              {t("results.lookup.open")}
            </Button>
          </form>

          {/* jump to emission from a demande id */}
          {canEmit && (
            <form
              onSubmit={(e) => {
                e.preventDefault();
                openEmission();
              }}
              style={{ display: "flex", flexDirection: "column", gap: "var(--nv-s-3)" }}
            >
              <FormField
                label={t("results.lookup.demandeId")}
                hint={t("results.lookup.demandeIdHint")}
                {...(demandeError ? { error: demandeError } : {})}
              >
                {(p) => (
                  <Input
                    {...p}
                    mono
                    inputMode="numeric"
                    autoComplete="off"
                    value={demandeId}
                    placeholder="456"
                    onChange={(e) => setDemandeId(e.target.value)}
                  />
                )}
              </FormField>
              <Button type="submit" variant="secondary" leadingIcon="✚">
                {t("results.lookup.emit")}
              </Button>
            </form>
          )}
        </div>
      </Card>

      <Card
        title={t("results.recent.title")}
        actions={
          recents.length > 0 ? (
            <Button size="sm" variant="ghost" onClick={clearRecents}>
              {t("results.recent.clear")}
            </Button>
          ) : undefined
        }
      >
        {recents.length === 0 ? (
          <p style={{ margin: 0, color: "var(--nv-slate)" }}>{t("results.recent.empty")}</p>
        ) : (
          <ul
            style={{
              listStyle: "none",
              margin: 0,
              padding: 0,
              display: "flex",
              flexDirection: "column",
              gap: "var(--nv-s-2)",
            }}
          >
            {recents.map((r) => (
              <li key={r.id}>
                <button
                  type="button"
                  onClick={() => navigate(`/resultats/comptes-rendus/${r.id}`)}
                  disabled={!online}
                  style={{
                    width: "100%",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    gap: "var(--nv-s-3)",
                    padding: "var(--nv-s-3)",
                    border: "1px solid var(--nv-line)",
                    borderRadius: "var(--nv-r-sm)",
                    background: "var(--nv-surface)",
                    color: "var(--nv-ink)",
                    cursor: online ? "pointer" : "not-allowed",
                    textAlign: "start",
                  }}
                >
                  <span style={{ display: "flex", alignItems: "center", gap: "var(--nv-s-3)" }}>
                    <span className="nv-mono nv-ltr-island" style={{ fontWeight: 600 }}>
                      {r.numero}
                    </span>
                    <span style={{ color: "var(--nv-slate)", fontSize: "var(--nv-fs-caption)" }}>
                      {t("results.version", { n: r.version })}
                    </span>
                    <EtatBadge etat={r.etat} />
                  </span>
                  <span style={{ color: "var(--nv-steel)", fontSize: "var(--nv-fs-caption)" }}>
                    {t("results.recent.openedAt", { date: formatDateTime(new Date(r.openedAt).toISOString(), locale) })}
                  </span>
                </button>
              </li>
            ))}
          </ul>
        )}
      </Card>
    </div>
  );
}
